import { useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch, apiFetchRaw, post } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";
import { appendListParams, unwrapList, type ListParams, type ListResponse } from "@/lib/list-params";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Connector {
  id: string;
  name: string;
  slug: string;
  description: string;
  /** The TIER — "managed" or "Custom" — not the browse taxonomy. That is `categoryKey`. */
  category: string;
  /** Browse taxonomy key from @fastn/core's CONNECTOR_CATEGORIES. Optional only
   *  because the embedded mount is handed rows it did not fetch. */
  categoryKey?: string;
  icon: string;
  domain?: string;
  authType: string;
  status: string;
  baseUrl: string;
  version: string;
  user_id?: string;
  org_id?: string;
  stage?: string;
  labels?: string[];
  authMethods?: Array<{
    id: string;
    type: string;
    title: string;
    isDefault: boolean;
    inputContract: Record<string, unknown>;
    authConfig: Record<string, unknown>;
    /** Provider's auth docs — "where do I get this key?" link on the connect page. */
    docsUrl?: string | null;
  }>;
  /** Read-only action executed at connect time to prove credentials work. */
  connectionVerifyActionId?: string | null;
  verifySuccessPath?: string | null;
  isMultiConnection?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: ConnectorActor | null;
  updatedBy?: ConnectorActor | null;
  publishedBy?: ConnectorActor | null;
  publishedAt?: string | null;
}

export interface ConnectorActor {
  id: string;
  name: string | null;
  email: string | null;
}

export interface ConnectorActivityEntry {
  at: string;
  actor: ConnectorActor | null;
  action: string;
  target: { type: "connector" | "action"; id: string; name: string };
  summary: string | null;
  version?: string;
  stage?: string;
}

export interface ConnectorActivityResponse {
  activity: ConnectorActivityEntry[];
}

export interface HealthStatus {
  status: string;
  statusCode: number;
  latencyMs: number;
  error: string | null;
  checkedAt: string;
}

export interface ConnectorExecution {
  id: string;
  connectorId: string;
  method: string;
  path: string;
  responseStatus: number;
  durationMs: number;
  createdAt: string;
}

export interface TestRequest {
  connectorId: string;
  method: string;
  path: string;
  body?: unknown;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * The WHOLE catalog in one array, for the pickers and selection modals that would
 * silently truncate under pagination. See `useConnectorsList` below for the paginated
 * sibling the Connectors table renders.
 *
 * This is the most expensive read the dashboard makes: measured on prod it is 612 kB and
 * ~1.6s for 373 connectors, because the list carries full detail per row. So it is worth
 * asking whether a caller needs the ARRAY before reaching for it:
 *
 *   - a count -> `useConnectorCount()`, which fetches one row and reads the total
 *   - a list nobody has opened yet -> pass `{ enabled: false }` until they do
 */
export function useConnectors(opts: { enabled?: boolean } = {}) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.connectors.list(orgId),
    queryFn: () => apiFetch<Connector[]>(`${API_BASE}/api/v1/connectors`),
    enabled: !!orgId && opts.enabled !== false,
  });
}

/**
 * How many connectors the caller can see, without downloading them.
 *
 * `?limit=1` returns one row and the full filtered count in `pagination.total`, which is
 * what the list endpoint computes anyway. The badge this backs used to mount the whole
 * catalog on every page (the Sidebar is global), spending 612 kB to render an integer.
 *
 * Its own query key: sharing `useConnectors`' key would let a one-row response overwrite
 * the cached full array that every picker reads.
 */
export function useConnectorCount() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: [...keys.connectors.list(orgId), "count"] as const,
    queryFn: () => apiFetchRaw<ListResponse<Connector>>(`${API_BASE}/api/v1/connectors?limit=1&offset=0`),
    enabled: !!orgId,
    select: (r: ListResponse<Connector>) => unwrapList(r).total,
  });
}

/**
 * Server-searched, server-paginated connectors — what the Connectors list
 * renders.
 *
 * Deliberately a sibling of `useConnectors()` rather than a replacement: that
 * hook backs sixteen dropdowns, pickers and selection modals which need the
 * whole catalog in one array. Paginating it there would silently truncate every
 * one of them, and a connector missing from a picker is far harder to notice
 * than a missing pager.
 */
export function useConnectorsList(
  params: ListParams & { labels?: string[]; includeDeleted?: boolean; categoryKey?: string } = {},
) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const { search, limit, offset, labels, includeDeleted, categoryKey } = params;

  const qs = appendListParams(new URLSearchParams(), { search, limit, offset });
  if (labels?.length) qs.set("labels", labels.join(","));
  if (includeDeleted) qs.set("includeDeleted", "true");
  // `categoryKey`, not `category`: the API's `category` field carries the connector
  // TIER ("managed"/"Custom"), so the browse taxonomy needed a name of its own.
  if (categoryKey) qs.set("categoryKey", categoryKey);

  return useQuery({
    // Every param that changes the result set is in the key. Without limit and
    // offset here, Next and Previous re-serve the page already in cache.
    queryKey: [
      ...keys.connectors.list(orgId),
      search ?? null,
      labels?.join(",") ?? null,
      includeDeleted ?? false,
      categoryKey ?? null,
      limit ?? null,
      offset ?? null,
    ] as const,
    queryFn: () => apiFetchRaw<ListResponse<Connector>>(`${API_BASE}/api/v1/connectors?${qs.toString()}`),
    enabled: !!orgId,
    // Hold the current page on screen while the next one loads, so paging and
    // typing do not blank the table and jump the scroll position.
    placeholderData: (prev) => prev,
    select: unwrapList,
  });
}

/** Fetch a single connector (with its authMethods) by id. Used by the focused
 * /connect/:connectorId page so it can render the connect flow without loading
 * the full connector list. */
export function useConnector(connectorId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.connectors.detail(orgId, connectorId),
    queryFn: () => apiFetch<Connector>(`${API_BASE}/api/v1/connectors/${connectorId}`),
    enabled: !!orgId && !!connectorId,
  });
}

export interface ConnectorEntity {
  id: string;
  connectorId: string;
  name: string;
  slug: string;
  description?: string;
}

/** Data objects a connector exposes (Opportunity, Account, Ticket, …). Empty
 *  for connectors that have never been modelled — callers should fall back to
 *  the connector's actions rather than treating that as an error. */
export function useConnectorEntities(connectorId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.connectors.entities(orgId, connectorId),
    queryFn: async () => {
      const res = await apiFetch<{ data: ConnectorEntity[] }>(
        `${API_BASE}/api/v1/entities?connectorId=${encodeURIComponent(connectorId!)}`,
      );
      return res.data ?? [];
    },
    enabled: !!orgId && !!connectorId,
  });
}

export interface AuthProvider {
  id: string;
  name: string;
  slug: string;
  providerType: string;
  clientId: string;
  scopes: string[];
  connectorId?: string;
  authorizationUrl?: string;
  isDefault?: boolean;
  oauthVariables?: Array<{ key: string; label: string; description?: string; placeholder?: string }>;
}

/** OAuth auth providers configured for a connector — feeds ConnectModal. */
export function useAuthProviders(connectorId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.authProviders.list(orgId, connectorId),
    queryFn: () =>
      apiFetch<AuthProvider[]>(
        `${API_BASE}/api/v1/auth-providers?connectorId=${encodeURIComponent(connectorId ?? "")}`,
      ),
    enabled: !!orgId && !!connectorId,
  });
}

export interface ConnectorActionListItem {
  id: string;
  slug: string;
  name: string;
  version: string;
  description?: string | null;
}

/**
 * List every active action for a connector (slug-based, cascade-aware).
 * Used by the workflow editor's Connector Manifest popup so developers can
 * add actions beyond what the static code-extractor finds.
 */
export function useConnectorActions(connectorSlug: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: ["connectors", orgId, "actions", connectorSlug ?? null] as const,
    queryFn: () =>
      apiFetch<ConnectorActionListItem[]>(
        `${API_BASE}/api/v2/connectors/${encodeURIComponent(connectorSlug ?? "")}/actions`,
      ),
    enabled: !!orgId && !!connectorSlug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectorNameMap(): Record<string, string> {
  const { data: connectors } = useConnectors();
  return useMemo(
    () => Object.fromEntries((connectors ?? []).map((c) => [c.slug, c.name])),
    [connectors],
  );
}

export function useConnectorHealth(connectorId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.connectors.health(orgId, connectorId),
    queryFn: () =>
      apiFetch<HealthStatus>(`${API_BASE}/api/v1/connectors/${connectorId}/health`),
    enabled: !!orgId && !!connectorId,
    staleTime: 10_000, // health is more volatile — refresh every 10 s
  });
}

export function useConnectorExecutions(connectorId: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.connectors.executions(orgId, connectorId),
    queryFn: () =>
      apiFetch<ConnectorExecution[]>(
        `${API_BASE}/api/v1/executions?connectorId=${connectorId}`,
      ),
    enabled: !!orgId && !!connectorId,
  });
}

export function useConnectorActivity(
  connectorId: string | undefined,
  actionId?: string,
  enabled = true,
) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.connectors.activity(orgId, connectorId, actionId),
    queryFn: () => {
      const qs = actionId ? `?actionId=${encodeURIComponent(actionId)}` : "";
      return apiFetch<ConnectorActivityResponse>(
        `${API_BASE}/api/v1/connectors/${connectorId}/activity${qs}`,
      );
    },
    enabled: enabled && !!orgId && !!connectorId,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useActivateConnector() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (connectorId: string) =>
      apiFetch(`${API_BASE}/api/v1/connectors/${connectorId}/activate`, post({})),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.connectors.all(orgId) }),
  });
}

export function useTestConnector() {
  return useMutation({
    mutationFn: (req: TestRequest) =>
      apiFetch<Record<string, unknown>>(`${API_BASE}/api/v1/execute`, post(req)),
  });
}
